// src/routes/Trades.tsx
//
// Phase 4 §4.11 — Trade UI.
//
// Sections:
//   • Propose: build proposed/requested asset lists (player or pick) and submit.
//   • Inbox  (incoming): proposals where I'm the receiver. Accept / reject.
//   • Outbox (outgoing): proposals I sent. Cancel.
//   • Accepted: completed trades; commissioner can undo.
//
// Asset IDs are entered as raw strings (Id<'players'> / Id<'futureDraftPicks'>).
// Phase 4 ships a minimal UI; richer pickers (player search, pick chooser)
// arrive in later polish phases.
import { useMutation, useQuery } from 'convex/react';
import { type FormEvent, useState } from 'react';
import { api } from '../../convex/_generated/api';
import type { Doc, Id } from '../../convex/_generated/dataModel';

type AssetDraft = { kind: 'player'; playerId: string } | { kind: 'pick'; pickId: string };

type TradeAssetDoc =
  | { kind: 'player'; playerId: Id<'players'> }
  | { kind: 'pick'; pickId: Id<'futureDraftPicks'> };

export default function Trades() {
  const me = useQuery(api.franchises.myFranchise);
  const franchises = useQuery(api.franchises.listFranchises);
  const isCommissioner = useQuery(api.franchises.amICommissioner);
  const incoming = useQuery(api.trades.listIncoming, {});
  const outgoing = useQuery(api.trades.listOutgoing, {});
  const accepted = useQuery(api.trades.listAccepted, {});
  const propose = useMutation(api.trades.proposeTrade);
  const respond = useMutation(api.trades.respondToTrade);
  const cancel = useMutation(api.trades.cancelTrade);
  const commissionerUndo = useMutation(api.trades.commissionerUndo);

  const [receiverId, setReceiverId] = useState<string>('');
  const [proposedAssets, setProposedAssets] = useState<AssetDraft[]>([]);
  const [requestedAssets, setRequestedAssets] = useState<AssetDraft[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (me === undefined) return <div className="p-6">Loading…</div>;
  if (me === null) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">No franchise</h1>
        <p className="mt-2 text-sm text-gray-600">
          Claim a franchise first.{' '}
          <a className="underline" href="/franchises">
            Pick one →
          </a>
        </p>
      </div>
    );
  }

  const franchisesList = (franchises ?? []) as Doc<'franchises'>[];
  const fById = new Map(franchisesList.map((f) => [f._id, f] as const));
  const otherFranchises = franchisesList.filter((f) => f._id !== me._id);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!receiverId) {
      setError('Pick a receiver franchise.');
      return;
    }
    if (proposedAssets.length === 0 && requestedAssets.length === 0) {
      setError('Trade must include at least one asset.');
      return;
    }
    setSubmitting(true);
    try {
      await propose({
        receiverFranchiseId: receiverId as Id<'franchises'>,
        proposedAssets: proposedAssets.map(toDocAsset),
        requestedAssets: requestedAssets.map(toDocAsset),
      });
      setProposedAssets([]);
      setRequestedAssets([]);
      setReceiverId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const wrap = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold">Trades</h1>
      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="border rounded p-4 bg-white">
        <h2 className="font-semibold mb-3">Propose a trade</h2>
        <form onSubmit={submit} className="space-y-3">
          <label className="flex flex-col text-sm max-w-md">
            <span className="text-gray-600 mb-1">Receiver</span>
            <select
              value={receiverId}
              onChange={(e) => setReceiverId(e.target.value)}
              className="border rounded px-2 py-1"
            >
              <option value="">— select —</option>
              {otherFranchises.map((f) => (
                <option key={f._id} value={f._id}>
                  {f.name} ({f.division})
                </option>
              ))}
            </select>
          </label>
          <div className="grid gap-4 md:grid-cols-2">
            <AssetEditor
              label="I'm offering"
              assets={proposedAssets}
              onChange={setProposedAssets}
            />
            <AssetEditor label="I want" assets={requestedAssets} onChange={setRequestedAssets} />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="px-3 py-2 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'Propose Trade'}
          </button>
        </form>
      </section>

      <section>
        <h2 className="font-semibold mb-2">Inbox (received proposals)</h2>
        {incoming === undefined ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : incoming.length === 0 ? (
          <p className="text-sm text-gray-500">No incoming proposals.</p>
        ) : (
          <ul className="space-y-2">
            {(incoming as Doc<'trades'>[]).map((t) => (
              <li key={t._id} className="border rounded p-3 bg-white">
                <div className="text-sm">
                  From{' '}
                  <strong>
                    {fById.get(t.proposerFranchiseId)?.name ?? String(t.proposerFranchiseId)}
                  </strong>
                </div>
                <TradeAssetSummary
                  proposed={t.proposedAssets as TradeAssetDoc[]}
                  requested={t.requestedAssets as TradeAssetDoc[]}
                  perspective="incoming"
                />
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => wrap(() => respond({ tradeId: t._id, response: 'accept' }))}
                    className="text-sm px-2 py-1 bg-green-600 text-white rounded"
                  >
                    accept
                  </button>
                  <button
                    type="button"
                    onClick={() => wrap(() => respond({ tradeId: t._id, response: 'reject' }))}
                    className="text-sm px-2 py-1 bg-red-600 text-white rounded"
                  >
                    reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="font-semibold mb-2">Outbox (sent proposals)</h2>
        {outgoing === undefined ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : outgoing.length === 0 ? (
          <p className="text-sm text-gray-500">No outgoing proposals.</p>
        ) : (
          <ul className="space-y-2">
            {(outgoing as Doc<'trades'>[]).map((t) => (
              <li key={t._id} className="border rounded p-3 bg-white">
                <div className="text-sm">
                  To{' '}
                  <strong>
                    {fById.get(t.receiverFranchiseId)?.name ?? String(t.receiverFranchiseId)}
                  </strong>{' '}
                  — {t.status}
                </div>
                <TradeAssetSummary
                  proposed={t.proposedAssets as TradeAssetDoc[]}
                  requested={t.requestedAssets as TradeAssetDoc[]}
                  perspective="outgoing"
                />
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => wrap(() => cancel({ tradeId: t._id }))}
                    className="text-sm text-red-600 hover:underline"
                  >
                    cancel
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="font-semibold mb-2">Accepted</h2>
        {accepted === undefined ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : accepted.length === 0 ? (
          <p className="text-sm text-gray-500">No accepted trades.</p>
        ) : (
          <ul className="space-y-2">
            {(accepted as Doc<'trades'>[]).map((t) => (
              <li key={t._id} className="border rounded p-3 bg-white">
                <div className="text-sm">
                  <strong>
                    {fById.get(t.proposerFranchiseId)?.name ?? String(t.proposerFranchiseId)}
                  </strong>{' '}
                  ↔{' '}
                  <strong>
                    {fById.get(t.receiverFranchiseId)?.name ?? String(t.receiverFranchiseId)}
                  </strong>
                </div>
                <TradeAssetSummary
                  proposed={t.proposedAssets as TradeAssetDoc[]}
                  requested={t.requestedAssets as TradeAssetDoc[]}
                  perspective="incoming"
                />
                {isCommissioner && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        const reason = window.prompt('Reason for undo (required):');
                        if (!reason) return;
                        wrap(() => commissionerUndo({ tradeId: t._id, reason }));
                      }}
                      className="text-sm text-orange-700 hover:underline"
                    >
                      commissioner undo
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function toDocAsset(a: AssetDraft): TradeAssetDoc {
  return a.kind === 'player'
    ? { kind: 'player', playerId: a.playerId as Id<'players'> }
    : { kind: 'pick', pickId: a.pickId as Id<'futureDraftPicks'> };
}

function AssetEditor({
  label,
  assets,
  onChange,
}: {
  label: string;
  assets: AssetDraft[];
  onChange: (next: AssetDraft[]) => void;
}) {
  return (
    <div>
      <div className="font-medium text-sm mb-1">{label}</div>
      <ul className="space-y-1 mb-2">
        {assets.length === 0 && <li className="text-sm text-gray-500 italic">none yet</li>}
        {assets.map((a, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: AssetDraft has no stable id; index is fine for an editable list.
          <li key={i} className="flex gap-2 items-center text-sm">
            <span className="font-mono text-xs">
              {a.kind === 'player' ? `Player ${a.playerId}` : `Pick ${a.pickId}`}
            </span>
            <button
              type="button"
              onClick={() => onChange(assets.filter((_, j) => j !== i))}
              className="text-xs text-red-600 hover:underline"
            >
              remove
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-3 text-sm">
        <button
          type="button"
          className="text-blue-600 hover:underline"
          onClick={() => {
            const id = window.prompt('Player ID (Id<"players">):');
            if (id) onChange([...assets, { kind: 'player', playerId: id }]);
          }}
        >
          + player
        </button>
        <button
          type="button"
          className="text-blue-600 hover:underline"
          onClick={() => {
            const id = window.prompt('Pick ID (Id<"futureDraftPicks">):');
            if (id) onChange([...assets, { kind: 'pick', pickId: id }]);
          }}
        >
          + pick
        </button>
      </div>
    </div>
  );
}

function TradeAssetSummary({
  proposed,
  requested,
  perspective,
}: {
  proposed: TradeAssetDoc[];
  requested: TradeAssetDoc[];
  perspective: 'incoming' | 'outgoing';
}) {
  const lhsLabel = perspective === 'incoming' ? 'They send' : 'I send';
  const rhsLabel = perspective === 'incoming' ? 'They want' : 'I get';
  return (
    <div className="mt-1 grid grid-cols-2 gap-2 text-xs text-gray-600">
      <div>
        <div className="uppercase tracking-wide text-gray-400">{lhsLabel}</div>
        <ul>
          {proposed.length === 0 && <li className="italic">nothing</li>}
          {proposed.map((a, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: assets have no stable id within a trade list.
            <li key={i} className="font-mono">
              {a.kind === 'player' ? `player ${a.playerId}` : `pick ${a.pickId}`}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="uppercase tracking-wide text-gray-400">{rhsLabel}</div>
        <ul>
          {requested.length === 0 && <li className="italic">nothing</li>}
          {requested.map((a, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: assets have no stable id within a trade list.
            <li key={i} className="font-mono">
              {a.kind === 'player' ? `player ${a.playerId}` : `pick ${a.pickId}`}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
